import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { axiosInstance } from '../../helpers'
import {
    PROPERTY_PIPELINE_CONFIGS_KEY,
    orgPipelineConfigsKey,
} from '../LeasingSeason/Configurations/PipelineBuilder/usePipelineConfigApi'
import type {
    ApplicationWorkbookTemplateDetail,
    ApplicationWorkbookTemplateListItem,
    EzosWorkbookField,
    OrgApplicationWorkbookDetail,
    OrgApplicationWorkbookListItem,
    PropertyApplicationWorkbookDetail,
    PropertyApplicationWorkbookListItem,
    WorkbookDefinition,
} from './workbook.types'

export const PROPERTY_APPLICATION_WORKBOOKS_KEY =
    'property-application-workbooks'
export const PROPERTY_APPLICATION_WORKBOOK_KEY = 'property-application-workbook'
export const ORG_APPLICATION_WORKBOOKS_KEY = 'org-application-workbooks'
export const ORG_APPLICATION_WORKBOOK_KEY = 'org-application-workbook'
export const APPLICATION_WORKBOOK_TEMPLATES_KEY =
    'application-workbook-templates'
export const APPLICATION_WORKBOOK_TEMPLATE_KEY = 'application-workbook-template'
export const EZOS_WORKBOOK_FIELDS_KEY = 'ezos-workbook-fields'

export const applicationWorkbookTemplatesKey = () =>
    [APPLICATION_WORKBOOK_TEMPLATES_KEY] as const

export const propertyApplicationWorkbooksKey = (
    apartmentId: number | null | undefined,
) => [PROPERTY_APPLICATION_WORKBOOKS_KEY, apartmentId] as const

export const propertyApplicationWorkbookKey = (
    apartmentId: number | null | undefined,
    workbookId: number | null | undefined,
) => [PROPERTY_APPLICATION_WORKBOOK_KEY, apartmentId, workbookId] as const

export const orgApplicationWorkbooksKey = (
    organizationId: number | null | undefined,
) => [ORG_APPLICATION_WORKBOOKS_KEY, organizationId] as const

export const orgApplicationWorkbookKey = (
    organizationId: number | null | undefined,
    workbookId: number | null | undefined,
) => [ORG_APPLICATION_WORKBOOK_KEY, organizationId, workbookId] as const

export function useApplicationWorkbookTemplates() {
    return useQuery({
        queryKey: applicationWorkbookTemplatesKey(),
        queryFn: async () => {
            const { data } = await axiosInstance.get<
                ApplicationWorkbookTemplateListItem[]
            >('/company/application-workbook-templates/')
            return data
        },
        refetchOnWindowFocus: false,
    })
}

export function usePropertyApplicationWorkbooks(
    apartmentId: number | null | undefined,
) {
    return useQuery({
        queryKey: propertyApplicationWorkbooksKey(apartmentId),
        queryFn: async () => {
            const { data } = await axiosInstance.get<
                PropertyApplicationWorkbookListItem[]
            >(`/company/apartment/${apartmentId}/application-workbooks/`)
            return data
        },
        enabled: apartmentId != null,
        refetchOnWindowFocus: false,
    })
}

export function usePropertyApplicationWorkbook(
    apartmentId: number | null | undefined,
    workbookId: number | null | undefined,
) {
    return useQuery({
        queryKey: propertyApplicationWorkbookKey(apartmentId, workbookId),
        queryFn: async () => {
            const {
                data,
            } = await axiosInstance.get<PropertyApplicationWorkbookDetail>(
                `/company/apartment/${apartmentId}/application-workbooks/${workbookId}/`,
            )
            return data
        },
        enabled: apartmentId != null && workbookId != null,
        refetchOnWindowFocus: false,
    })
}

export function useSavePropertyApplicationWorkbook(
    apartmentId: number | null | undefined,
    workbookId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (definition: WorkbookDefinition) => {
            const {
                data,
            } = await axiosInstance.patch<PropertyApplicationWorkbookDetail>(
                `/company/apartment/${apartmentId}/application-workbooks/${workbookId}/`,
                { definition },
            )
            return data
        },
        onSuccess: (data) => {
            queryClient.setQueryData(
                propertyApplicationWorkbookKey(apartmentId, workbookId),
                data,
            )
            queryClient.invalidateQueries({
                queryKey: propertyApplicationWorkbooksKey(apartmentId),
            })
        },
    })
}

export function useCreatePropertyApplicationWorkbook(
    apartmentId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            name: string
            template_key?: string
            definition?: WorkbookDefinition
        }) => {
            const body: {
                name: string
                template_key?: string
                definition?: WorkbookDefinition
            } = { name: payload.name }

            if (payload.template_key) {
                body.template_key = payload.template_key
            } else if (payload.definition) {
                body.definition = payload.definition
            }

            const {
                data,
            } = await axiosInstance.post<PropertyApplicationWorkbookDetail>(
                `/company/apartment/${apartmentId}/application-workbooks/`,
                body,
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: propertyApplicationWorkbooksKey(apartmentId),
            })
        },
    })
}

export function useOrgApplicationWorkbooks(
    organizationId: number | null | undefined,
) {
    return useQuery({
        queryKey: orgApplicationWorkbooksKey(organizationId),
        queryFn: async () => {
            const { data } = await axiosInstance.get<
                OrgApplicationWorkbookListItem[]
            >(`/company/organization/${organizationId}/application-workbooks/`)
            return data
        },
        enabled: organizationId != null,
        refetchOnWindowFocus: false,
    })
}

export function useOrgApplicationWorkbook(
    organizationId: number | null | undefined,
    workbookId: number | null | undefined,
) {
    return useQuery({
        queryKey: orgApplicationWorkbookKey(organizationId, workbookId),
        queryFn: async () => {
            const {
                data,
            } = await axiosInstance.get<OrgApplicationWorkbookDetail>(
                `/company/organization/${organizationId}/application-workbooks/${workbookId}/`,
            )
            return data
        },
        enabled: organizationId != null && workbookId != null,
        refetchOnWindowFocus: false,
    })
}

export function useSaveOrgApplicationWorkbook(
    organizationId: number | null | undefined,
    workbookId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (definition: WorkbookDefinition) => {
            const {
                data,
            } = await axiosInstance.patch<OrgApplicationWorkbookDetail>(
                `/company/organization/${organizationId}/application-workbooks/${workbookId}/`,
                { definition },
            )
            return data
        },
        onSuccess: (data) => {
            queryClient.setQueryData(
                orgApplicationWorkbookKey(organizationId, workbookId),
                data,
            )
            queryClient.invalidateQueries({
                queryKey: orgApplicationWorkbooksKey(organizationId),
            })
        },
    })
}

export function useCreateOrgApplicationWorkbook(
    organizationId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            name: string
            template_key?: string
            definition?: WorkbookDefinition
        }) => {
            const body: {
                name: string
                template_key?: string
                definition?: WorkbookDefinition
            } = { name: payload.name }

            if (payload.template_key) {
                body.template_key = payload.template_key
            } else if (payload.definition) {
                body.definition = payload.definition
            }

            const {
                data,
            } = await axiosInstance.post<OrgApplicationWorkbookDetail>(
                `/company/organization/${organizationId}/application-workbooks/`,
                body,
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: orgApplicationWorkbooksKey(organizationId),
            })
        },
    })
}

export interface OrgWorkbookApplyResult {
    created: number
    updated: number
    skipped_locked: number
    skipped_missing: number
    skipped_forked?: number
}

export function useApplyOrgWorkbookToProperties(
    organizationId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            workbookId: number
            property_ids: number[]
        }) => {
            const { data } = await axiosInstance.post<OrgWorkbookApplyResult>(
                `/company/organization/${organizationId}/application-workbooks/${payload.workbookId}/apply/`,
                { property_ids: payload.property_ids },
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PROPERTY_APPLICATION_WORKBOOKS_KEY],
            })
            queryClient.invalidateQueries({
                queryKey: orgApplicationWorkbooksKey(organizationId),
            })
        },
    })
}

export function useApplyOrgWorkbookToAll(
    organizationId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            workbookId: number
            force_overwrite_forked?: boolean
        }) => {
            const { data } = await axiosInstance.post<OrgWorkbookApplyResult>(
                `/company/organization/${organizationId}/application-workbooks/${payload.workbookId}/apply-to-all/`,
                {
                    force_overwrite_forked:
                        payload.force_overwrite_forked ?? false,
                },
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PROPERTY_APPLICATION_WORKBOOKS_KEY],
            })
            queryClient.invalidateQueries({
                queryKey: orgApplicationWorkbooksKey(organizationId),
            })
        },
    })
}

export function useCopyPropertyApplicationWorkbook(
    apartmentId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            sourceWorkbookId: number
            name?: string
            createdFromSeasonId?: number
        }) => {
            if (apartmentId == null) {
                throw new Error('Apartment is required.')
            }

            const {
                data,
            } = await axiosInstance.post<PropertyApplicationWorkbookDetail>(
                `/company/apartment/${apartmentId}/application-workbooks/${payload.sourceWorkbookId}/copy/`,
                {
                    name: payload.name,
                    created_from_season_id: payload.createdFromSeasonId,
                },
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: propertyApplicationWorkbooksKey(apartmentId),
            })
        },
    })
}

export interface ConfigBundlePropertyResult {
    property_id: number
    pipeline?: {
        status: string
        config_id?: number
        name?: string
        detail?: string
    } | null
    workbook?: {
        status: string
        workbook_id?: number | null
        name?: string
    } | null
    signable_docs?: Array<{
        template_key: string
        status: string
        signable_doc_id?: number
        detail?: string
    }>
    error?: string
}

export interface ConfigBundleApplyResult {
    properties: ConfigBundlePropertyResult[]
    pipeline_created: number
    pipeline_updated?: number
    pipeline_skipped_locked?: number
    pipeline_errors: number
    signable_docs_created: number
    signable_docs_errors: number
    workbook_created: number
    workbook_updated: number
    workbook_skipped_locked: number
    workbook_skipped_missing: number
}

export function useApplyOrgConfigBundle(
    organizationId: number | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            property_ids: number[]
            org_workbook_id?: number
            org_pipeline_config_id?: number
            pipeline_source_config_id?: number
            pipeline_name?: string
            catalog_template_keys?: string[]
            signable_doc_overrides?: Record<string, number>
        }) => {
            const { data } = await axiosInstance.post<ConfigBundleApplyResult>(
                `/company/organization/${organizationId}/config-bundle/apply/`,
                payload,
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [PROPERTY_APPLICATION_WORKBOOKS_KEY],
            })
            queryClient.invalidateQueries({
                queryKey: [PROPERTY_PIPELINE_CONFIGS_KEY],
            })
            queryClient.invalidateQueries({
                queryKey: orgPipelineConfigsKey(organizationId),
            })
        },
    })
}

export const applicationWorkbookTemplateKey = (templateKey: string) =>
    [APPLICATION_WORKBOOK_TEMPLATE_KEY, templateKey] as const

export const ezosWorkbookFieldsKey = () => [EZOS_WORKBOOK_FIELDS_KEY] as const

export function useApplicationWorkbookTemplate(
    templateKey: string | null | undefined,
) {
    return useQuery({
        queryKey: applicationWorkbookTemplateKey(templateKey || ''),
        queryFn: async () => {
            const {
                data,
            } = await axiosInstance.get<ApplicationWorkbookTemplateDetail>(
                `/company/application-workbook-templates/${templateKey}/`,
            )
            return data
        },
        enabled: !!templateKey,
        refetchOnWindowFocus: false,
    })
}

export function useSaveApplicationWorkbookTemplate(
    templateKey: string | null | undefined,
) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            definition: WorkbookDefinition
            name?: string
            description?: string
        }) => {
            const {
                data,
            } = await axiosInstance.patch<ApplicationWorkbookTemplateDetail>(
                `/company/application-workbook-templates/${templateKey}/`,
                payload,
            )
            return data
        },
        onSuccess: (data) => {
            queryClient.setQueryData(
                applicationWorkbookTemplateKey(templateKey || ''),
                data,
            )
            queryClient.invalidateQueries({
                queryKey: applicationWorkbookTemplatesKey(),
            })
        },
    })
}

export function useEzosWorkbookFields(activeOnly = false) {
    return useQuery({
        queryKey: [...ezosWorkbookFieldsKey(), { activeOnly }],
        queryFn: async () => {
            const { data } = await axiosInstance.get<EzosWorkbookField[]>(
                '/company/ezos-workbook-fields/',
                { params: activeOnly ? { active_only: true } : undefined },
            )
            return data
        },
        refetchOnWindowFocus: false,
    })
}

export function useCreateEzosWorkbookField() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            field_id?: string
            question_text: string
            input_type: string
            options?: string[]
            default_section_id?: string
            pinned?: boolean
        }) => {
            const { data } = await axiosInstance.post<EzosWorkbookField>(
                '/company/ezos-workbook-fields/',
                payload,
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ezosWorkbookFieldsKey() })
        },
    })
}

export function useUpdateEzosWorkbookField() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: {
            field_id: string
            question_text?: string
            input_type?: string
            options?: string[]
            default_section_id?: string
            is_active?: boolean
            pinned?: boolean
            new_field_id?: string
        }) => {
            const { field_id, ...body } = payload
            const { data } = await axiosInstance.patch<EzosWorkbookField>(
                `/company/ezos-workbook-fields/${encodeURIComponent(
                    field_id,
                )}/`,
                body,
            )
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ezosWorkbookFieldsKey() })
        },
    })
}

export function useCheckEzosWorkbookFieldId() {
    return useMutation({
        mutationFn: async (payload: {
            field_id?: string
            question_text?: string
        }) => {
            const { data } = await axiosInstance.get<{
                field_id: string
                suggested_field_id: string
                collision: null | {
                    field_id: string
                    existing: EzosWorkbookField
                }
            }>('/company/ezos-workbook-fields/check/', { params: payload })
            return data
        },
    })
}
